import * as angular from 'angular';
import {AgentModel} from "../angular/models/agent.model";
import {Document} from "../../commons/models/document.model";
import {Reminder} from "../../commons/models/reminder.model";
import {BringozApi} from "../../persistence-layer/models/bringoz-api.model";
import {IHttpService, IPromise, IQService} from "angular";
import {$agentFactory} from "../$agent.factory";
import {$agentsFactory} from "../$agents.factory";
import {SplitService} from "../../commons/services/split-service.service";
import {AgentsPaginationQueryModel} from "../angular/models/agents-pagination-query.model";
import {ApiConfig} from "../../commons/models/api-config.model";
import {AgentAuthType} from "../types/agentAuth.type";
import {AgentClientType} from "../types/agentClient.type";
import {BulkPreferencesUpdateQuery} from "../models/queries.model";

export class AgentRequests {

  /* @ngInject */
  constructor(private AgentsAPI: BringozApi, private LocationAPI, private apiConfig: ApiConfig,
              private $agent: $agentFactory, private $agents: $agentsFactory, private $q: IQService,
              private $http: IHttpService,
              private splitService: SplitService, private Upload) {
  }

  getAll(options) {
    return this.$agents().$getAll(options);
  }

  getAgentsPagination(queryPayload: AgentsPaginationQueryModel) {
    const api = this.AgentsAPI.actions.getAgentsPagination;
    return this.$$callAgents(api, queryPayload, {}, {});
  }

  getOne(agentId: string) {
    const api = this.AgentsAPI.actions.getOne;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  createOne(agent: AgentModel) {
    const api = this.AgentsAPI.actions.createOne;
    return this.$$callAgents(api, {}, agent, {});
  }

  assignAgentsToProfile(profileId: string, agentIds: string[]) {
    const api = this.AgentsAPI.actions.assignAgents;
    return this.$$callAgents(api, {profileId: profileId}, {ids: agentIds}, {});
  }

  updateOne(agentId: string, agent: AgentModel) {
    const api = this.AgentsAPI.actions.updateOne;
    return this.$$callAgent(api, {}, agent, {}, agentId);
  }

  getAgentCompletedTaskByDate(agentId: string, timestamp) {
    const api = this.AgentsAPI.actions.completedTasks;
    return this.$$callAgent(api, {
      date: timestamp,
      zoneId: this.splitService.getTimeZone()
    }, {}, {}, agentId);
  }

  getAgentPreferences(agentId: string) {
    const api = this.AgentsAPI.actions.getPreferences;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  setAgentPreferences(preferences, agentId: string) {
    const api = this.AgentsAPI.actions.setPreferences;
    return this.$$callAgent(api, {}, preferences, {}, agentId);
  }

  changeAgentsStatus(status, agentIds: string[]) {
    const api = this.AgentsAPI.actions[`changeStatusTo_${status}`];
    return this.$$callAgents(api, {ids: agentIds}, {}, {});
  }

  refreshAgentItinerary(agentId: string, ignoreTimeWindow: boolean) {
    const api = this.AgentsAPI.actions.refresh;
    return this.$$callAgent(api, {ignoreTimeWindow: ignoreTimeWindow}, {}, {}, agentId);
  }

  recalcAgentItinerary(agentId: string) {
    const api = this.AgentsAPI.actions.recalc;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  getAgreements(agentId: string) {
    const api = this.AgentsAPI.actions.getAgreements;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  getRequirements(agentId: string): IPromise<any> {
    const api = this.AgentsAPI.actions.getRequirements;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  postRequirements(agentId: string, requirement, sendSms: boolean, sendEmail: boolean) {
    const api = this.AgentsAPI.actions.postRequirements;
    return this.$$callAgent(api, {
      sendSms: sendSms,
      sendEmail: sendEmail
    }, requirement, {requirementId: requirement.id}, agentId);
  }

  exportPending() {
    const api = this.AgentsAPI.actions.exportPending;
    return this.$$callAgents(api);
  }

  addDepots(depotIds: string[], agentIds: string[]) {
    const api = this.AgentsAPI.actions.addDepots;
    return this.$$callAgents(api, {}, {depotIds: depotIds, agentIds: agentIds}, {});
  }

  addBranches(branchIds: string[], agentIds: string[]) {
    const api = this.AgentsAPI.actions.addBranches;
    return this.$$callAgents(api, {}, {branchIds: branchIds, agentIds: agentIds}, {});
  }

  getItinerary(agentId: string) {
    const api = this.AgentsAPI.actions.getItinerary;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  editItineraryLock(agentId: string) {
    const api = this.AgentsAPI.actions.editItineraryLock;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  editItineraryUnlock(agentId: string) {
    const api = this.AgentsAPI.actions.editItineraryUnlock;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  checkEditItinerary(agentId: string, payload) {
    const api = this.AgentsAPI.actions.editItinerary;
    return this.$$callAgent(api, {save: false}, payload, {}, agentId);
  }

  saveEditItinerary(agentId: string, payload) {
    const api = this.AgentsAPI.actions.editItinerary;
    return this.$$callAgent(api, {save: true}, payload, {}, agentId);
  }

  editItineraryReset(agentId: string) {
    const api = this.AgentsAPI.actions.editItineraryReset;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  editItinerarySettings(agentId: string, itinerary) {
    const api = this.AgentsAPI.actions.editItinerarySettings;
    return this.$$callAgent(api, {}, itinerary, {}, agentId);
  }

  getAgentTotals(query): IPromise<any> {
    const api = this.AgentsAPI.actions.getTotals;
    return this.$$callAgents(api, query, {}, {});
  }

  getAgentTotalsByConfigurationId(configurationId): IPromise<any> {
    const api = this.AgentsAPI.actions.getTotalsAssigned;
    return this.$$callAgents(api, {configurationId: configurationId}, {}, {});
  }

  getAgentsByBranch(branchId) {
    const api = this.AgentsAPI.actions.getAgentsByBranch;
    return this.$$callAgents(api, {branchIds: [branchId]}, {}, {});
  }

  deleteAgent(agentId: string, shouldMask) {
    const api = this.AgentsAPI.actions.deleteAgent;
    return this.$$callAgents(api, {mask: shouldMask}, {}, {id: agentId});
  }

  updateCarrier(agentId: string, carrierId: string) {
    const api = this.AgentsAPI.actions.updateCarrier;
    return this.$$callAgents(api, {}, {carrierId: carrierId}, {id: agentId});
  }

  updateAgentLocation(agentId: string, lat, lng) {
    const api = this.AgentsAPI.actions.updateAgentLocation;
    return this.$$callAgent(api, {}, {lat: lat, lng: lng}, {}, agentId);
  }

  updatePassword(agentId: string, password: string) {
    const api = this.AgentsAPI.actions.updatePassword;
    return this.$$callAgent(api, {}, {password: password}, {}, agentId);
  }

  selectScheduleTemplate(agentId: string, scheduleTemplateId) {
    const api = this.AgentsAPI.actions.selectScheduleTemplate;
    return this.$$callAgent(api, {selectedScheduleId: scheduleTemplateId}, {}, {}, agentId);
  }

  clearSchedule(agentId: string) {
    const api = this.AgentsAPI.actions.clearSchedule;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  getAgentBalance(agentId: string) {
    const api = this.AgentsAPI.actions.getAgentBalance;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }


  addAgentsToTeam(teamIds: string[], agentIds: string[]) {
    const api = this.AgentsAPI.actions.addAgentsToTeam;
    return this.$$callAgent(api, {}, {
      teamIds,
      agentIds
    }, {});
  }

  getAgentCrossSiteData(email: string, id: string) {
    const api = this.AgentsAPI.actions.getAgentCrossSiteData;
    return this.$$callAgent(api, {}, {
      email,
      id
    }, {});
  }

  removeAgentsFromTeam(teamIds: string[], agentIds: string[]) {
    const api = this.AgentsAPI.actions.removeAgentsFromTeam;
    return this.$$callAgent(api, {}, {
      teamIds,
      agentIds
    }, {});
  }


  getAgentPaymentCollectionTotals(agentId: string, from, to, transactionType, accountId: string) {
    const api = this.AgentsAPI.actions.getPaymentTransactionsTotals;
    return this.$$callAgent(api,
      {from: from, to: to, type: transactionType, accountId: accountId},
      {},
      {},
      agentId);
  }

  getAllAgentsPaymentTotals(from?, to?, transactionType?, accountId?: string) {
    const api = this.AgentsAPI.actions.getAllAgentsPaymentTotals;
    return this.$$callAgent(api,
      {from: from, to: to, type: transactionType, accountId: accountId},
      {},
      {});
  }

  addPaymentTransaction(agentId: string, transaction) {
    const api = this.AgentsAPI.actions.addPaymentTransaction;
    return this.$$callAgent(api, {}, transaction, {}, agentId);
  }

  bulkPreferencesUpdate(preferences, query: BulkPreferencesUpdateQuery) {
    const api = this.AgentsAPI.actions.bulkPreferencesUpdate;
    return this.$$callAgents(api, query, preferences, {});
  }

  sendNotification(agentPnType, selectedIds, excludedIds, branchIds, noBranches, status, agentType, agentSkills?: string[],
                   gpsStatus?: boolean, gpsPermissions?: string[], isActive?: boolean, sessionRecording?: boolean, noArea?: boolean, serviceAreaIds?: string[]) {
    const api = this.AgentsAPI.actions.sendNotification;
    return this.$$callAgents(api, {
      ids: selectedIds,
      excludedIds: excludedIds,
      branchIds: branchIds,
      noBranches: noBranches,
      status: status,
      agentType: agentType,
      skill: agentSkills,
      gpsStatus: gpsStatus,
      gpsPermission: gpsPermissions,
      isActive: isActive,
      sessionRecording: sessionRecording,
      noArea: noArea,
      areaIds: serviceAreaIds
    }, {agentPnType}, {});
  }

  updateAgentAssistants(agent: AgentModel) {
    const api = this.AgentsAPI.actions.updateAgentAssistants;
    return this.$$callAgent(api, {}, agent, {}, agent.id);
  }

  updateAgentExternalId(agent: AgentModel) {
    const api = this.AgentsAPI.actions.updateAgentExternalId;
    return this.$$callAgent(api, {}, agent, {}, agent.id);
  }


  //returns a list of the Agent's assistants by the Agent id
  getAgentAssistants(agentId: string) {
    const api = this.AgentsAPI.actions.getAgentAssistants;
    return this.$$callAgent(api, {}, {}, {}, agentId)
  }

  getAgentLocation(agentId: string): IPromise<any> {
    const siteId = this.splitService.getSiteId();
    const url = this.apiConfig.baseUrl + this.LocationAPI.base + '/agent';
    const deferred = this.$q.defer();
    this.$http.get(url, {
      params: {
        siteId: siteId,
        ids: agentId
      }
    })
      .then((res) => {
        deferred.resolve(res.data);
      })
      .catch((error) => {
        deferred.reject(error);
      });

    return deferred.promise;
  }

  uploadAgents(file) {
    const siteId = this.splitService.getSiteId();
    return this.Upload.upload({
      url: this.apiConfig.baseUrl + `/importers-agents/validate`,
      method: 'POST',
      params: {siteId: siteId},
      data: {file: file}
    })
      .then((response) => {
        return this.$q.when(response.data);
      })
  }

  exportAgents(exportType, status, agentType: string, branchIds, noBranches, isActive: boolean, sessionRecording: boolean, areaIds, gpsStatus, gpsPermissions) {
    const api = this.AgentsAPI.actions.exportAgents;
    return this.$$callAgents(api, {
      exportType: exportType,
      branchIds: branchIds,
      noBranches: noBranches,
      status: status,
      agentType: agentType,
      isActive: isActive,
      sessionRecording: sessionRecording,
      areaIds: areaIds,
      gpsStatus: gpsStatus,
      gpsPermission: gpsPermissions
    }, {}, {});
  }

  //////////////////////////////////////// DOCUMENTS ////////////////////////////////////////

  getDocuments(agentId: string) {
    const api = this.AgentsAPI.actions.getDocuments;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  createDocument(document: Document, agentId: string) {
    const api = this.AgentsAPI.actions.createDocument;
    return this.$$callAgent(api, {}, document, {}, agentId);
  }

  updateDocument(document: Document, agentId: string) {
    const api = this.AgentsAPI.actions.updateDocument;
    return this.$$callAgent(api, {}, document, {documentId: document.id}, agentId);
  }

  deleteDocument(documentId: string, agentId: string) {
    const api = this.AgentsAPI.actions.deleteDocument;
    return this.$$callAgent(api, {}, {}, {documentId: documentId}, agentId);
  }

  //////////////////////////////////////// REMINDERS ////////////////////////////////////////

  getReminders(agentId: string) {
    const api = this.AgentsAPI.actions.getReminders;
    return this.$$callAgent(api, {}, {}, {}, agentId);
  }

  createReminder(reminder: Reminder, agentId: string) {
    const api = this.AgentsAPI.actions.createReminder;
    return this.$$callAgent(api, {}, reminder, {}, agentId);
  }

  updateReminder(reminder: Reminder, agentId: string) {
    const api = this.AgentsAPI.actions.updateReminder;
    return this.$$callAgent(api, {}, reminder, {reminderId: reminder.id}, agentId);
  }

  deleteReminder(reminderId: string, agentId: string) {
    const api = this.AgentsAPI.actions.deleteReminder;
    return this.$$callAgent(api, {}, {}, {reminderId: reminderId}, agentId);
  }

  passwordless(agentId: string, authType: AgentAuthType, client: AgentClientType) {
    const api = this.AgentsAPI.actions.passwordless;
    return this.$$callAgent(api, {authType: authType, client: client}, {}, {id: agentId});
  }

  getAgentPerformance(agentId: string, from: number, to: number) {
    const api = this.AgentsAPI.actions.agentPerformance;
    return this.$$callAgent(api, {
      from: from,
      to: to
    }, {}, {id: agentId});
  }


  // ******************************
  // Internal methods
  // ******************************

  $$callAgent(_apiCall, _params?, _data?, _options?, agentId?) {
    return this.$agent(agentId).$call(_apiCall, _params, _data, _options);
  }

  $$callAgents(_apiCall, _params?, _data?, _options?) {
    return this.$agents().$call(_apiCall, _params, _data, _options);
  }

}

angular
  .module('bringoz.admin.agents')
  .service('agentRequests', AgentRequests);
